import { useEffect, useRef, useState } from 'react'
import { GripVertical, Search, X } from 'lucide-react'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import invariant from 'tiny-invariant'
import { useTranslation } from '@kostbar/i18n'
import type { Product } from '@/api/types'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency } from '@/shared/utils/format-currency'
import { useProducts } from '@/features/products/hooks/use-products'
import { useCategories } from '@/features/categories/hooks/use-categories'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ProductSelectorPanelProps {
  restaurantId: number
}

interface DraggableProductCardProps {
  product: Product
}

function DraggableProductCard({ product }: DraggableProductCardProps) {
  const { t: tMenus } = useTranslation('menus')
  const [isDragging, setIsDragging] = useState(false)
  const productRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const productEl = productRef.current
    invariant(productEl)

    return draggable({
      element: productEl,
      getInitialData: () => ({
        type: 'product',
        productId: product.id,
        product,
      }),
      onDragStart: () => {
        setIsDragging(true)
      },
      onDrop: () => {
        setIsDragging(false)
      },
    })
  }, [product])

  const style = {
    opacity: isDragging ? 0.5 : 1,
  }

  const guestName = product.publicName ?? product.name

  return (
    <div
      ref={productRef}
      className="p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-move transition-colors"
      style={style}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="font-medium text-sm leading-tight">{guestName}</p>
            {product.categoryCode && (
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {product.categoryCode}
              </Badge>
            )}
          </div>
          <p className="truncate text-xs text-muted-foreground">
            {tMenus('productSelector.sku')} {product.sku}
            {product.name !== guestName && (
              <>
                {' '}
                · {tMenus('itemRow.internalName')} {product.name}
              </>
            )}
          </p>
          {product.currentPrice !== undefined && (
            <p className="mt-1 text-xs font-medium">
              {formatCurrency(product.currentPrice)}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export function ProductSelectorPanel({
  restaurantId,
}: ProductSelectorPanelProps) {
  const { t: tMenus } = useTranslation('menus')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const { data: productsData, isLoading: productsLoading } = useProducts({
    restaurantId,
    search: searchQuery || undefined,
    categoryCode: selectedCategory !== 'all' ? selectedCategory : undefined,
    // A merged duplicate (CHG-2026-09-duplicate-product-merge) is not independently sellable, so
    // it must not be offered here — only its primary can be added to a menu.
    excludeMerged: true,
  })

  const { data: categoriesData } = useCategories()

  const products = productsData?.items || []
  const categories = categoriesData?.items || []

  return (
    <div className="w-96 border-l bg-muted/20 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-background">
        <h2 className="text-lg font-semibold mb-4">
          {tMenus('productSelector.title')}
        </h2>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={tMenus('productSelector.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setSearchQuery('')}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Category filter */}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger>
            <SelectValue
              placeholder={tMenus('productSelector.allCategories')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {tMenus('productSelector.allCategories')}
            </SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.code} value={category.code}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto p-4">
        {productsLoading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            {tMenus('productSelector.loading')}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">
              {searchQuery
                ? tMenus('productSelector.noProductsFound')
                : tMenus('productSelector.noProductsAvailable')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {products.map((product) => (
              <DraggableProductCard key={product.sku} product={product} />
            ))}
          </div>
        )}
      </div>

      {/* Helper text */}
      <div className="p-4 border-t bg-muted/50">
        <p className="text-xs text-muted-foreground">
          {tMenus('productSelector.hint')}
        </p>
      </div>
    </div>
  )
}
